import { ArrowUpDown, Loader2 } from "lucide-react";
import { useState } from "react";
import { Skeleton } from "../../../../components/ui/common/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../components/ui/common/table";
import { TooltipProvider } from "../../../../components/ui/common/tooltip";
import { cn } from "../../../../lib/utils";
import { RecipeGroupRecord, RecipeListRowHandlers, SortField } from "../types";
import RecipeListTableRow from "./RecipeListTableRow";
import {
  getRecipeListColumnCount,
  RECIPE_LIST_SORT_COLUMNS,
  recipeListTableColumnClass,
  recipeListTableHeaderColumnClass
} from "./recipeListTable.columns";

interface RecipeListTableProps {
  isAdmin: boolean;
  currentUserId?: string;
  isInitialLoading: boolean;
  isRefetching: boolean;
  records: RecipeGroupRecord[];
  onSort: (field: SortField) => void;
  handlers: RecipeListRowHandlers;
}

const SortableHeader = ({
  label,
  field,
  onSort,
  disabled,
  align = "left"
}: {
  label: string;
  field: SortField;
  onSort: (field: SortField) => void;
  disabled?: boolean;
  align?: "left" | "right" | "center";
}) => (
  <button
    type="button"
    onClick={() => onSort(field)}
    disabled={disabled}
    className={cn(
      "tw-flex tw-w-full tw-items-center tw-gap-1 tw-font-semibold tw-text-primary hover:tw-text-primary/80 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-whitespace-normal tw-break-words",
      align === "right" && "tw-justify-end",
      align === "center" && "tw-justify-center",
      align === "left" && "tw-justify-start"
    )}
  >
    {label}
    <ArrowUpDown className="tw-h-3 tw-w-3 tw-shrink-0" />
  </button>
);

const RecipeListTableSkeletonRow = () => (
  <TableRow>
    <TableCell className={recipeListTableColumnClass.expand}>
      <Skeleton className="tw-h-5 tw-w-5 tw-rounded-full" />
    </TableCell>
    <TableCell className={recipeListTableColumnClass.recipeName}>
      <Skeleton className="tw-h-4 tw-w-36" />
    </TableCell>
    <TableCell className={recipeListTableColumnClass.variation}>
      <Skeleton className="tw-h-4 tw-w-20" />
    </TableCell>
    <TableCell className={recipeListTableColumnClass.numeric}>
      <Skeleton className="tw-mx-auto tw-h-4 tw-w-16" />
    </TableCell>
    <TableCell className={recipeListTableColumnClass.numeric}>
      <Skeleton className="tw-mx-auto tw-h-4 tw-w-16" />
    </TableCell>
    <TableCell className={recipeListTableColumnClass.impactChange}>
      <Skeleton className="tw-mx-auto tw-h-4 tw-w-12" />
    </TableCell>
    <TableCell className={recipeListTableColumnClass.recipeType}>
      <Skeleton className="tw-mx-auto tw-h-5 tw-w-16" />
    </TableCell>
    <TableCell className={recipeListTableColumnClass.date}>
      <Skeleton className="tw-mx-auto tw-h-4 tw-w-20" />
    </TableCell>
    <TableCell className={recipeListTableColumnClass.date}>
      <Skeleton className="tw-mx-auto tw-h-4 tw-w-20" />
    </TableCell>
    <TableCell className={recipeListTableColumnClass.createdBy}>
      <Skeleton className="tw-h-4 tw-w-8" />
    </TableCell>
    <TableCell className={recipeListTableColumnClass.actions}>
      <div className="tw-flex tw-justify-start tw-gap-1">
        <Skeleton className="tw-h-8 tw-w-8 tw-rounded-md" />
        <Skeleton className="tw-h-8 tw-w-8 tw-rounded-md" />
      </div>
    </TableCell>
  </TableRow>
);

const RecipeListTable = ({
  isAdmin,
  currentUserId,
  isInitialLoading,
  isRefetching,
  records,
  onSort,
  handlers
}: RecipeListTableProps) => {
  const [expandedRecipeIds, setExpandedRecipeIds] = useState<Set<string>>(new Set());
  const columnCount = getRecipeListColumnCount();
  const showEmptyState = !isInitialLoading && !isRefetching && records.length === 0;
  const isBusy = isInitialLoading || isRefetching;

  const toggleExpanded = (recipeId: string) => {
    setExpandedRecipeIds(current => {
      const next = new Set(current);
      if (next.has(recipeId)) {
        next.delete(recipeId);
      } else {
        next.add(recipeId);
      }
      return next;
    });
  };

  return (
    <div className="tw-relative tw-border tw-border-border tw-rounded-lg tw-overflow-x-auto tw-bg-white tw-min-h-[280px]">
      {isRefetching && (
        <div
          className="tw-absolute tw-inset-0 tw-z-20 tw-flex tw-items-center tw-justify-center tw-bg-white/75 tw-backdrop-blur-[1px]"
          role="status"
          aria-live="polite"
          aria-label="Loading recipes"
        >
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-primary/20 tw-bg-white tw-px-8 tw-py-5 tw-shadow-md">
            <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-primary" />
            <p className="tw-text-sm tw-font-medium tw-text-primary">Loading recipes...</p>
          </div>
        </div>
      )}

      <TooltipProvider delayDuration={200}>
        <Table
          className={cn(
            "tw-table-fixed tw-w-full [&_td]:tw-px-2 [&_td]:tw-py-3 [&_th]:tw-px-2 [&_th]:tw-py-3",
            isRefetching && "tw-pointer-events-none"
          )}
        >
        <TableHeader>
          <TableRow className="tw-border-b-0 tw-bg-primary/1 hover:tw-bg-primary/1 [&_th]:tw-bg-primary/5 [&_th]:tw-text-primary [&_th]:tw-align-middle">
            <TableHead className={recipeListTableHeaderColumnClass.expand} />
            {RECIPE_LIST_SORT_COLUMNS.map(({ label, field, headerClass, align }) => (
              <TableHead key={field} className={headerClass}>
                <SortableHeader
                  label={label}
                  field={field}
                  onSort={onSort}
                  disabled={isBusy}
                  align={align}
                />
              </TableHead>
            ))}
            <TableHead className={recipeListTableHeaderColumnClass.actions}>
              <div className="tw-flex tw-w-full tw-items-center tw-justify-center tw-font-semibold tw-text-primary">
                Actions
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isInitialLoading ? (
            [...Array(5)].map((_, index) => <RecipeListTableSkeletonRow key={index} />)
          ) : showEmptyState ? (
            <TableRow>
              <TableCell colSpan={columnCount} className="tw-text-center tw-py-12 tw-text-muted-foreground">
                No recipes found. Click &quot;New Recipe&quot; to get started.
              </TableCell>
            </TableRow>
          ) : (
            records.map(recipe => (
              <RecipeListTableRow
                key={recipe.id}
                recipe={recipe}
                isAdmin={isAdmin}
                currentUserId={currentUserId}
                handlers={handlers}
                isExpanded={expandedRecipeIds.has(recipe.id)}
                onToggleExpand={() => toggleExpanded(recipe.id)}
              />
            ))
          )}
        </TableBody>
        </Table>
      </TooltipProvider>
    </div>
  );
};

export default RecipeListTable;
